import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  useCSCategoryTemplatesPaged,
  useCSCategoryTemplatesPagination,
  useCSCategoryTemplatesPageFilters,
  useCSCategoryTemplateFilterOptions,
  useCreateCSCategoryTemplate,
  useUpdateCSCategoryTemplate,
  useSetCSCategoryTemplateEnabled,
  useDeleteCSCategoryTemplate
} from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CSCategory, CSCategoryPropagateAction } from '@/types';
import { CSIntentsTable, CSIntentFormDialog, CSIntentDeleteDialog, CSIntentPropagateDialog } from '@/modules/cs-intents/components';

interface PropagateState {
  templateId: string;
  action: CSCategoryPropagateAction;
}

export default function CSIntentsPage() {
  const { data, isLoading } = useCSCategoryTemplatesPaged();
  const [{ page, limit }, setPagination] = useCSCategoryTemplatesPagination();
  const [filters, setFilters] = useCSCategoryTemplatesPageFilters();
  const { data: filterOptions } = useCSCategoryTemplateFilterOptions();

  const createMutation = useCreateCSCategoryTemplate();
  const updateMutation = useUpdateCSCategoryTemplate();
  const setEnabledMutation = useSetCSCategoryTemplateEnabled();
  const deleteMutation = useDeleteCSCategoryTemplate();

  const intents = data?.categories ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingIntent, setEditingIntent] = useState<CSCategory | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [propagateState, setPropagateState] = useState<PropagateState | null>(null);

  const [searchInput, setSearchInput] = useState(filters.name ?? '');

  // Debounce name search — reset to page 1 on change
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, name: searchInput || undefined }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const categoryOptions = useMemo(() => filterOptions?.categories ?? [], [filterOptions]);
  const subcategoryOptions = useMemo(() => filterOptions?.subcategories ?? [], [filterOptions]);

  function handleCategoryChange(value: string) {
    setFilters((prev) => ({ ...prev, category: value === 'all' ? undefined : value, subcategory: undefined }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  function handleSubcategoryChange(value: string) {
    setFilters((prev) => ({ ...prev, subcategory: value === 'all' ? undefined : value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  function openCreate() {
    setEditingIntent(null);
    setSheetOpen(true);
  }

  function openEdit(intent: CSCategory) {
    setEditingIntent(intent);
    setSheetOpen(true);
  }

  function handleSaved(intent: CSCategory, wasCreate: boolean) {
    setPropagateState({ templateId: intent.id, action: wasCreate ? 'create' : 'update' });
  }

  async function handleToggleEnabled(intent: CSCategory) {
    const nextEnabled = !(intent.isEnabled !== false);
    try {
      await setEnabledMutation.mutateAsync({ id: intent.id, isEnabled: nextEnabled });
      toast.success(nextEnabled ? 'Intent enabled' : 'Intent disabled');
      setPropagateState({ templateId: intent.id, action: nextEnabled ? 'enable' : 'disable' });
    } catch (err) {
      console.error('CSIntentsPage.handleToggleEnabled failed', { intentId: intent.id, nextEnabled, error: err });
      toast.error(getApiErrorMessage(err, 'Failed to update intent'));
    }
  }

  function handleDeleted(id: string) {
    setPropagateState({ templateId: id, action: 'delete' });
  }

  return (
    <div className='flex h-full flex-col gap-6 p-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-base font-semibold'>CS Intents</h1>
          <p className='text-xs text-muted-foreground'>Manage the shared intent templates every brand is seeded from</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className='mr-2 h-4 w-4' />
          Add Intent
        </Button>
      </div>

      <div className='flex items-center gap-4'>
        <SearchInput
          placeholder='Search by name...'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className='w-[280px] rounded-md'
        />

        <Select value={filters.category ?? 'all'} onValueChange={handleCategoryChange}>
          <SelectTrigger className='w-[200px] border border-border bg-white'>
            <SelectValue placeholder='All Categories' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Categories</SelectItem>
            {categoryOptions.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.subcategory ?? 'all'} onValueChange={handleSubcategoryChange}>
          <SelectTrigger className='w-[200px] border border-border bg-white'>
            <SelectValue placeholder='All Subcategories' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Subcategories</SelectItem>
            {subcategoryOptions.map((sub) => (
              <SelectItem key={sub} value={sub}>
                {sub}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='flex-1'>
        <CSIntentsTable
          intents={intents}
          isLoading={isLoading}
          onEdit={openEdit}
          onDelete={setDeletingId}
          onToggleEnabled={handleToggleEnabled}
          total={total}
          page={page}
          limit={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
          onLimitChange={(newLimit) => setPagination({ page: 1, limit: newLimit })}
        />
      </div>

      <CSIntentFormDialog
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editingIntent={editingIntent}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(data) => createMutation.mutateAsync(data)}
        onUpdate={(id, data) => updateMutation.mutateAsync({ id, data })}
        onSaved={handleSaved}
      />

      <CSIntentDeleteDialog
        deletingId={deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        isDeleting={deleteMutation.isPending}
        onDelete={(id) => deleteMutation.mutateAsync(id)}
        onDeleted={handleDeleted}
      />

      <CSIntentPropagateDialog
        templateId={propagateState?.templateId ?? null}
        action={propagateState?.action ?? null}
        onOpenChange={(open) => !open && setPropagateState(null)}
      />
    </div>
  );
}
